import React, { useState, useEffect } from 'react'
import DocumentTypeForm from '../components/DocumentTypeForm'
import { apiService } from '../services/apiService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentIcon,
  FolderIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const DocumentTypesPage = () => {
  const [documentTypes, setDocumentTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingType, setEditingType] = useState(null)

  useEffect(() => {
    loadDocumentTypes()
  }, [])

  const loadDocumentTypes = async () => {
    try {
      setLoading(true)
      const result = await apiService.getDocumentTypes()
      setDocumentTypes(result.data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de documento:', error)
      setDocumentTypes([])
      toast.error('Erro ao carregar tipos de documento')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingType(null)
    setShowForm(true)
  }

  const handleEdit = (documentType) => {
    setEditingType(documentType)
    setShowForm(true)
  }

  const handleView = (documentType) => {
    toast(`Visualizando tipo: ${documentType.name}`, {
      icon: 'ℹ️',
    })
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja desativar o tipo de documento "${name}"?`)) {
      return
    }

    try {
      await apiService.deleteDocumentType(id)
      toast.success('Tipo de documento desativado com sucesso!')
      loadDocumentTypes()
    } catch (error) {
      console.error('Erro ao desativar tipo de documento:', error)
      toast.error('Erro ao desativar tipo de documento')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingType(null)
  }

  const handleFormSuccess = () => {
    loadDocumentTypes()
    setShowForm(false)
    setEditingType(null)
  }

  const filteredTypes = documentTypes.filter(type => {
    const searchLower = searchQuery.toLowerCase()
    return (
      type.name.toLowerCase().includes(searchLower) ||
      (type.description && type.description.toLowerCase().includes(searchLower))
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando tipos de documento...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <DocumentIcon className="h-8 w-8 text-blue-600 mr-3" />
            Tipos de Documento
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie os tipos de documentos do sistema
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Novo Tipo</span>
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhum tipo encontrado' : 'Backend implementado'}
            </h3>
            <p className="text-gray-500 mb-4">
              API funcionando. Frontend completo em desenvolvimento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {type.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {type.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(type)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(type)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id, type.name)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Desativar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal do Formulário */}
      {showForm && (
        <DocumentTypeForm
          documentType={editingType}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}

export default DocumentTypesPage